import { useState, useCallback, useEffect, memo } from 'react';
import { WorkspaceContainerFieldAnnotation } from '@scale/shared/types/response/textcollection';
import { WorkspaceContainerField } from '@scale/shared/types/params/textcollection';
import WorkspaceContainerSection from 'react-scale/components/scalers/tasks/textcollection/WorkspaceContainerSection';
import { makeStyles, RadioGroup, Radio } from 'design-system';
import _ from 'lodash';
import FieldHeader from 'react-scale/components/scalers/tasks/textcollection/SimpleTextCollection/FieldHeader';

const NO_LANGUAGE_SELECTED = 'Please select a language';

export interface WorkspaceProps {
  field: WorkspaceContainerField;
  isCustomer?: boolean; // to control hidden test cases
  annotation: WorkspaceContainerFieldAnnotation;
  setAnnotation: (annotation: WorkspaceContainerFieldAnnotation) => void;
  setSubmitError?: (field_id: string, error: string) => void;
  disabled?: boolean;
  projectId?: string;
  assignmentId?: string;
  userId?: string;
}

export const WorkspaceSection = memo(
  ({
    field,
    isCustomer,
    annotation,
    setAnnotation,
    setSubmitError: _setSubmitError,
    disabled,
    projectId,
    assignmentId,
    userId,
  }: WorkspaceProps) => {
    const [language, setLanguage] = useState<string>(
      _.first(annotation?.response)?.selectedLanguage || '',
    );
    const [changedLanguageCounter, setChangedLanguageCounter] = useState<number>(0); // increment when language changes from a non empty value to another non empty value
    const [infoToolTipContent, setInfoToolTipContent] = useState<string | null>(null);
    const classes = useStyles();
    const languageToWorkspaceProjectIdMap =
      field.workspaceContainerConfig?.languageToWorkspaceProjectIdMap;

    const onLanguageChange = useCallback(
      (newLanguage: string) => {
        if (language) {
          setChangedLanguageCounter((prev: number) => prev + 1);
        }
        setLanguage(newLanguage);
      },
      [setLanguage, language, setChangedLanguageCounter],
    );

    const setSubmitError = useCallback(
      (error: string) => {
        _setSubmitError && _setSubmitError?.(field.field_id, error);
      },
      [_setSubmitError, field.field_id],
    );

    useEffect(() => {
      if (languageToWorkspaceProjectIdMap && !language && field.enforceCodeExecutionSuccess) {
        setSubmitError(NO_LANGUAGE_SELECTED);
      }
    }, [languageToWorkspaceProjectIdMap, language, field]);

    return (
      <div className={classes.container}>
        <FieldHeader
          title={field.title}
          description={field.description}
          hint={infoToolTipContent ?? field.hint}
        />
        {languageToWorkspaceProjectIdMap && (
          <div className={classes.body}>
            <div className={classes.languagePrompt}>
              Select a given language to create the workspace with
            </div>
            <RadioGroup value={language} onChange={onLanguageChange}>
              {Object.keys(languageToWorkspaceProjectIdMap).map((language: string) => (
                <Radio key={language} value={language} label={language} />
              ))}
            </RadioGroup>
          </div>
        )}
        {(!languageToWorkspaceProjectIdMap || language) && (
          <div className={classes.body}>
            {language && <div className={classes.header}>Instructions</div>}
            <WorkspaceContainerSection
              field={field}
              projectId={projectId}
              assignmentId={assignmentId}
              userId={userId}
              annotation={annotation}
              isCustomer={isCustomer}
              setAnnotation={setAnnotation}
              setSubmitError={_setSubmitError}
              disabled={disabled}
              selectedLanguage={language}
              changedLanguageCounter={changedLanguageCounter}
              setToolTipContent={setInfoToolTipContent}
            />
          </div>
        )}
      </div>
    );
  },
);

const useStyles = makeStyles(theme => ({
  body: {
    marginTop: theme.spacing(1),
  },
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  title: {
    color: '#000000',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    display: 'flex',
    flexDirection: 'row',
  },
  titleText: {
    fontSize: '14px',
    padding: '4px',
  },
  languagePrompt: {
    fontWeight: 400,
    fontSize: '13px',
    lineHeight: '20px',
    marginBottom: theme.spacing(1),
  },
  header: {
    fontWeight: 500,
    fontSize: '14px',
  },
  codeIcon: {
    paddingTop: '4px',
    paddingBottom: '4px',
    paddingRight: '4px',
    marginRight: '4px',
  },
  headerContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  infoToolTip: {
    paddingBottom: '12px',
    cursor: 'pointer',
  },
}));
